import {
  KnowledgePageVersionDiff,
  KnowledgeVersionInfo,
} from '@this+that/models';
import { StateCreator } from 'zustand';

import { KnowledgePageContentDetail } from '../../../services/knowledge.service';
import { BrainPageHistorySlice, BrainStore, PageHistoryEntry } from '../types';

const EMPTY_PAGE_HISTORY_ENTRY: PageHistoryEntry = {
  versions: [],
  cursor: null,
  hasMore: false,
  selectedVersion: null,
  currentVersion: null,
  loaded: false,
  diffByVersion: {},
  contentByVersion: {},
};

function buildMergedVersions(
  existing: KnowledgeVersionInfo[],
  incoming: KnowledgeVersionInfo[],
  append: boolean,
): KnowledgeVersionInfo[] {
  if (append) {
    return [...existing, ...incoming];
  }
  return incoming;
}

function getOrEmpty(
  state: { pageHistoryById: Record<string, PageHistoryEntry> },
  pageId: string,
): PageHistoryEntry {
  return state.pageHistoryById[pageId] ?? { ...EMPTY_PAGE_HISTORY_ENTRY };
}

export const createPageHistorySlice: StateCreator<
  BrainStore,
  [],
  [],
  BrainPageHistorySlice
> = (set) => ({
  pageHistoryById: {},

  setPageHistoryVersions: (pageId, payload) => {
    set((state) => {
      const existing = getOrEmpty(state, pageId);
      const versions = buildMergedVersions(
        existing.versions,
        payload.versions,
        payload.append,
      );
      return {
        pageHistoryById: {
          ...state.pageHistoryById,
          [pageId]: {
            ...existing,
            versions,
            cursor: payload.cursor,
            hasMore: payload.hasMore,
            currentVersion: payload.currentVersion,
            loaded: true,
          },
        },
      };
    });
  },

  setPageHistorySelectedVersion: (pageId, version) => {
    set((state) => {
      const existing = getOrEmpty(state, pageId);
      return {
        pageHistoryById: {
          ...state.pageHistoryById,
          [pageId]: { ...existing, selectedVersion: version },
        },
      };
    });
  },

  cachePageHistoryDiff: (
    pageId: string,
    version: number,
    diff: KnowledgePageVersionDiff,
  ) => {
    set((state) => {
      const existing = getOrEmpty(state, pageId);
      return {
        pageHistoryById: {
          ...state.pageHistoryById,
          [pageId]: {
            ...existing,
            diffByVersion: { ...existing.diffByVersion, [version]: diff },
          },
        },
      };
    });
  },

  cachePageHistoryContent: (
    pageId: string,
    version: number,
    content: KnowledgePageContentDetail,
  ) => {
    set((state) => {
      const existing = getOrEmpty(state, pageId);
      return {
        pageHistoryById: {
          ...state.pageHistoryById,
          [pageId]: {
            ...existing,
            contentByVersion: {
              ...existing.contentByVersion,
              [version]: content,
            },
          },
        },
      };
    });
  },

  resetPageHistory: (pageId: string) => {
    set((state) => {
      const next = { ...state.pageHistoryById };
      delete next[pageId];
      return { pageHistoryById: next };
    });
  },
});
